/* In-room real-time calls (MatrixRTC / LiveKit), ported from the prototype.

   Connects to the LiveKit SFU with a token minted by the Presence Engine
   (validated against the Matrix session). Renders participant tiles imperatively
   into a container the RoomView provides, and exposes reactive `callState` for
   the dock + mini-dock. livekit-client loads from a CDN on demand.

   NOTE: getUserMedia needs a secure context, so calls run on https / localhost,
   not over plain http. Same constraint as the prototype. */
import { reactive } from 'vue'
import { matrix } from '@/lib/matrix'
import { rawIcon } from '@/lib/icons'
import { useFloor } from '@/stores/floor'
import { usePresence } from '@/stores/presence'
import { usePrefs } from '@/stores/prefs'
import { useCapture } from '@/stores/capture'
import { playSfx } from '@/lib/sfx'

const SDK_URL = 'https://esm.sh/livekit-client@2'
const TAB_ID = Math.random().toString(36).slice(2, 8)

export interface CallParticipant {
  mxid: string
  you: boolean
  speaking: boolean
  micOn: boolean
  camOn: boolean
  screen: boolean
  deaf: boolean // sound output muted (their ears) — announced over the data channel
}

export interface CallState {
  connected: boolean
  mic: boolean
  cam: boolean
  screen: boolean
  deafen: boolean // sound output muted (your ears, not your mic)
  count: number
  roomSlug: string | null
  roomName: string
  message: string
  participants: CallParticipant[]
  // Bumped on every render pass (tracks subscribed/muted, people moving…) so
  // views that draw their own video (the watercooler bubbles) know to re-check
  // cameraTrackFor — the LiveKit track objects themselves aren't reactive.
  mediaTick: number
}

export const callState: CallState = reactive({
  connected: false, mic: false, cam: false, screen: false, deafen: false, count: 0,
  roomSlug: null, roomName: '', message: '', participants: [], mediaTick: 0,
})

/** How the call presents itself per room kind:
 *  - grid: the normal everyone-equal tile grid
 *  - spotlight: stage rooms — one main tile (screen share, else whoever is
 *    talking), everyone else in a small strip
 *  - hidden: the call runs audio-only and something else (the watercooler's
 *    spatial floor) draws the people */
export interface MountOpts {
  audio?: boolean // auto-enable the mic on connect (default true)
  layout?: 'grid' | 'spotlight' | 'hidden'
}

/* eslint-disable @typescript-eslint/no-explicit-any */
let LK: any = null
let room: any = null
let curMatrixId: string | null = null
let container: HTMLElement | null = null
let audioSink: HTMLDivElement | null = null
let gridObs: ResizeObserver | null = null // re-runs the grid layout when the stage resizes
let gridRaf = 0
let connecting = false
let lastMedia = ''
let heartbeat: ReturnType<typeof setInterval> | null = null
// The call we WANT to be in. Drives auto-reconnect: an unexpected drop or a
// failed connect (common during a page reload, while membership/sync are still
// settling) retries against this instead of giving up. Cleared by leaveCall.
let desired: { id: string; opts: Required<MountOpts> } | null = null
let reconnectTimer: ReturnType<typeof setTimeout> | null = null
let reconnectTries = 0
const RECONNECT_MAX = 8
let mountOpts: Required<MountOpts> = { audio: true, layout: 'grid' }
let lastMainSid: string | null = null // spotlight: keeps the main tile steady
let pinnedShareSid: string | null = null // which share the user chose to view full size
// ---- remembered mute state ----
// The mic-muted and deafen choices are the user's, not the call's: a deliberate
// mute shouldn't quietly undo itself when the call drops and auto-reconnects, or
// when the page reloads. We persist them apart from the SDK so they're known
// before any room exists and survive every reconnect. (The prefs/presence stores
// deliberately keep call app-state out, so the call layer owns this key.)
const CALL_LS_KEY = 'atrium.call.v1'
let micMuted = false // user wants the mic muted
let deafened = false // user wants sound output muted (their ears); newly subscribed audio respects it
let micMutedBeforeDeafen: boolean | null = null // mic state captured at deafen, restored on undeafen
try {
  const saved = JSON.parse(localStorage.getItem(CALL_LS_KEY) || '{}')
  micMuted = !!saved.micMuted
  deafened = !!saved.deafen
} catch { /* defaults */ }
function persistMute() {
  try { localStorage.setItem(CALL_LS_KEY, JSON.stringify({ micMuted, deafen: deafened })) } catch { /* non-fatal */ }
}
// ---- resume across reloads ----
// `desired` (the call we want to be in) is module state and dies with the page,
// and only the RoomView ever mounts a call — so reloading while looking at any
// other view (floor, mentions, …) silently dropped you out. Stamp the live call
// to localStorage (periodically and at pagehide) so bootstrap can rejoin it when
// the app comes straight back up. The short window keeps "reopened the tab next
// morning" from dropping you into a long-stale call.
const RESUME_LS_KEY = 'atrium.call.resume.v1'
const RESUME_WINDOW_MS = 45_000
let resumeStamp: ReturnType<typeof setInterval> | null = null
function stampResume() {
  if (!room || room.state !== 'connected' || !curMatrixId) return
  const payload = { id: curMatrixId, slug: useFloor().slugOf(curMatrixId), opts: mountOpts, ts: Date.now() }
  try { localStorage.setItem(RESUME_LS_KEY, JSON.stringify(payload)) } catch { /* non-fatal */ }
}
function clearResume() {
  try { localStorage.removeItem(RESUME_LS_KEY) } catch { /* */ }
}
// The page going away is NOT the call ending: livekit installs its own
// beforeunload/pagehide handlers (at connect, i.e. after ours) that disconnect
// the room, and that disconnect arrives as CLIENT_INITIATED — the same reason as
// a deliberate hang-up. Without this flag the Disconnected handler would clear
// the resume stamp on every reload, which is exactly when we need it. Our
// listeners registered first, so the stamp is written (and the flag raised)
// while the room is still connected.
let pageLeaving = false
function onPageLeave() { pageLeaving = true; stampResume() }
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', onPageLeave)
  window.addEventListener('pagehide', onPageLeave)
  // Back from the bfcache (or an aborted unload): this page lives on after all.
  window.addEventListener('pageshow', () => { pageLeaving = false })
}

/** Rejoin the call the page went away in (reload, quick close+reopen), called
 *  from App.vue's boot once the floor is loaded. Mounts into a detached element
 *  — opening the room later re-targets the container (mountCall is idempotent).
 *  No-op when a call is already live/wanted or the stamp is stale. */
export function resumeCall(): void {
  if (room || connecting || desired) return
  let saved: { id?: string; slug?: string | null; opts?: MountOpts; ts?: number } | null = null
  try { saved = JSON.parse(localStorage.getItem(RESUME_LS_KEY) || 'null') } catch { /* corrupt */ }
  if (!saved?.id || typeof saved.ts !== 'number' || Date.now() - saved.ts > RESUME_WINDOW_MS) {
    if (saved) clearResume()
    return
  }
  const floor = useFloor()
  // Reloaded straight into a DIFFERENT room? That room's view owns the call now.
  if (floor.activeSlug && saved.slug && floor.activeSlug !== saved.slug) { clearResume(); return }
  // Ambient persistence includes the floor store: you're still IN that room.
  if (!floor.activeSlug && saved.slug) floor.activeSlug = saved.slug
  reconnectTries = 1 // it's a reconnect, not a fresh join: no join cue
  void mountCall(document.createElement('div'), saved.id, saved.opts || {})
}

// Each remote's sound-output mute (their ears), keyed by full LiveKit identity.
// Deafen never touches the SFU, so peers announce it over the data channel; we
// hold the last value so the tiles can mark who isn't listening.
const remoteDeaf = new Map<string, boolean>()

/* While connected, re-publish m.rtc.member periodically so its `ts` stays
   fresh: the presence engine's ghost sweeper treats an old ts as abandoned
   when it cannot reach LiveKit to check. */
const HEARTBEAT_MS = 120_000
function startHeartbeat() {
  stopHeartbeat()
  stampResume() // connected: keep the reload-resume stamp fresh from here on
  resumeStamp = setInterval(stampResume, 15_000)
  heartbeat = setInterval(() => {
    if (!room || room.state !== 'connected' || !curMatrixId) return
    const floor = useFloor()
    const slug = floor.slugOf(curMatrixId)
    if (!slug) return
    const s = readState()
    void floor.publishMedia(slug, { mic: s.mic, cam: s.cam, screen: s.screen })
  }, HEARTBEAT_MS)
}
function stopHeartbeat() {
  if (heartbeat) { clearInterval(heartbeat); heartbeat = null }
  if (resumeStamp) { clearInterval(resumeStamp); resumeStamp = null }
}

function sink(): HTMLDivElement {
  if (!audioSink) {
    audioSink = document.createElement('div')
    audioSink.style.display = 'none'
    document.body.appendChild(audioSink)
  }
  return audioSink
}
async function loadSDK(): Promise<any> {
  if (LK) return LK
  const url = SDK_URL
  LK = await import(/* @vite-ignore */ url)
  return LK
}

const esc = (s = '') => String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c] as string))
const mxidOf = (identity: string) => (identity || '').split('|')[0]
function personName(identity: string): string {
  const mxid = mxidOf(identity)
  return useFloor().people[mxid]?.name || mxid.replace(/^@/, '').split(':')[0]
}
function personColor(identity: string): string {
  return useFloor().people[mxidOf(identity)]?.color || '#6CA8E0'
}
function initials(identity: string): string {
  const n = personName(identity)
  const parts = n.trim().split(/[\s-]+/).filter(Boolean)
  return ((parts[0] || '?')[0] + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase()
}

async function rtcToken(matrixRoomId: string, deviceTag: string): Promise<any> {
  const token = matrix.getSession()?.accessToken
  const res = await fetch('/atrium/api/rtc-token', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
    body: JSON.stringify({ room_id: matrixRoomId, device_tag: deviceTag || '' }),
  })
  if (!res.ok) throw new Error('rtc-token ' + res.status)
  return res.json()
}

function clearReconnect() {
  if (reconnectTimer) { clearTimeout(reconnectTimer); reconnectTimer = null }
}

/* ---- audio playback unlock ----
   Browsers block audio that starts without a user gesture — classically right
   after a reload, when resumeCall reconnects on its own. livekit attaches each
   subscribed track and calls play(); a blocked play() leaves THAT element
   paused forever, so you keep hearing whoever published after your first
   click while everyone already talking stays silent (until they rejoin and
   get a fresh element). The SDK tracks the failure (canPlaybackAudio) and
   retries every stuck element on startAudio(), which itself must run inside
   a user gesture — so arm a one-shot listener for the next click/keypress. */
let unlockArmed = false
function unlockAudio() {
  unlockArmed = false
  window.removeEventListener('pointerdown', unlockAudio, true)
  window.removeEventListener('keydown', unlockAudio, true)
  if (room && room.state === 'connected' && !room.canPlaybackAudio) {
    room.startAudio().catch(() => armAudioUnlock()) // still blocked: next gesture
  }
}
function armAudioUnlock() {
  if (unlockArmed || typeof window === 'undefined') return
  unlockArmed = true
  window.addEventListener('pointerdown', unlockAudio, true)
  window.addEventListener('keydown', unlockAudio, true)
}

// Get back into the call we still want (`desired`) after an unexpected drop or
// a failed attempt. Exponential backoff; gives up gracefully after RECONNECT_MAX
// so a genuinely-unavailable call doesn't loop forever.
function scheduleReconnect() {
  if (reconnectTimer || !desired || !container) return
  if (reconnectTries >= RECONNECT_MAX) { clearResume(); message('Live call unavailable. Reload to rejoin.'); return }
  const wait = Math.min(1000 * 2 ** reconnectTries, 15_000)
  reconnectTries++
  message('Reconnecting to the call…')
  reconnectTimer = setTimeout(() => {
    reconnectTimer = null
    if (desired && container) void mountCall(container, desired.id, desired.opts)
  }, wait)
}

// Some disconnects mean "stay out": we left on purpose, another device took the
// identity, or we were removed / the room closed. Everything else (network blip,
// server restart, signal close, a reload race) is worth reconnecting through.
function recoverableDisconnect(sdk: any, reason: any): boolean {
  const R = sdk.DisconnectReason
  if (!R || reason == null) return true
  return ![R.CLIENT_INITIATED, R.DUPLICATE_IDENTITY, R.PARTICIPANT_REMOVED, R.ROOM_DELETED, R.ROOM_CLOSED, R.USER_REJECTED].includes(reason)
}

export async function mountCall(el: HTMLElement, matrixRoomId: string, opts: MountOpts = {}): Promise<void> {
  container = el
  // Re-fit the grid whenever the stage changes size — window resize, the side
  // panel toggling, or crossing the mobile breakpoint (which switches layouts).
  if (typeof ResizeObserver === 'function') {
    gridObs?.disconnect()
    gridObs = new ResizeObserver(scheduleRelayout)
    gridObs.observe(el)
  }
  mountOpts = { audio: opts.audio !== false, layout: opts.layout || 'grid' }
  if (!matrixRoomId) { message('No room.'); return }
  if (room && curMatrixId === matrixRoomId && room.state === 'connected') { desired = { id: matrixRoomId, opts: mountOpts }; clearReconnect(); render(); return }
  if (room && curMatrixId !== matrixRoomId) await leaveCall()
  if (connecting) return
  desired = { id: matrixRoomId, opts: mountOpts }
  clearReconnect()
  connecting = true
  message('Connecting to the call…')
  // Play the join cue here, in the clear — before the SDK load, connect and
  // (in most rooms) the mic opening, which reconfigures the audio subsystem and
  // would clip/duck a cue overlapping it. reconnectTries === 0 means this is a
  // genuine join, not a silent auto-reconnect. We hold the mic-enable below until
  // the cue has finished (joinCueAt + joinCueMs), so a fast-connecting room can't
  // step on its second note.
  const joinCueMs = reconnectTries === 0 ? playSfx('selfJoin') : 0
  const joinCueAt = Date.now()
  try {
    const sdk = await loadSDK()
    const { token, url, ice_servers, force_relay } = await rtcToken(matrixRoomId, TAB_ID)
    if (!token) throw new Error('no token')
    const roomOpts: any = { adaptiveStream: true, dynacast: true }
    // Saved device choices ('' = browser default). Capture defaults apply when
    // a track is first acquired; audioOutput routes playback (where supported).
    const prefs = usePrefs()
    if (prefs.micId) roomOpts.audioCaptureDefaults = { deviceId: prefs.micId }
    if (prefs.camId) roomOpts.videoCaptureDefaults = { deviceId: prefs.camId }
    if (prefs.speakerId && canPickSpeaker()) roomOpts.audioOutput = { deviceId: prefs.speakerId }
    if (ice_servers?.length) {
      roomOpts.rtcConfig = { iceServers: ice_servers }
      if (force_relay) roomOpts.rtcConfig.iceTransportPolicy = 'relay'
    }
    room = new sdk.Room(roomOpts)
    curMatrixId = matrixRoomId
    wireRoom(sdk, room, matrixRoomId)
    await room.connect(url, token)
    // Focus/stage rooms join muted by design; everywhere else the mic opens —
    // unless you've chosen to stay muted or deafened, which we remember across
    // reconnects and reloads so the call can't quietly switch your mic back on.
    if (mountOpts.audio && !micMuted && !deafened) {
      // Let the join cue finish first — opening the mic ducks an overlapping cue.
      const wait = joinCueMs - (Date.now() - joinCueAt)
      if (wait > 0) await new Promise((r) => setTimeout(r, wait))
      try { await room.localParticipant.setMicrophoneEnabled(true) } catch { /* mic denied */ }
    }
    clearReconnect()
    reconnectTries = 0
    // Tracks that subscribed during connect may already have hit the autoplay
    // block, possibly before the event listener above was in place.
    if (!room.canPlaybackAudio) armAudioUnlock()
    // Re-assert the remembered deafen state on this fresh connection: mute any
    // already-attached audio and tell the room we're not listening. (New tracks
    // honour `deafened` as they attach in TrackSubscribed.)
    callState.deafen = deafened
    if (deafened) {
      sink().querySelectorAll('audio').forEach((a) => { (a as HTMLAudioElement).muted = true })
      broadcastDeaf()
    }
    render()
    emitState()
    startHeartbeat()
  } catch (e) {
    console.warn('[call] connect failed', e)
    // Tear the half-open room down before dropping the reference, so a failed
    // attempt can't keep firing events into a later, healthy connection.
    try { room?.removeAllListeners(); void room?.disconnect() } catch { /* */ }
    room = null; curMatrixId = null
    // Still want this call? Retry (membership/sync may just not be ready yet —
    // typical right after a reload). Otherwise report it and stay put.
    if (desired && desired.id === matrixRoomId) scheduleReconnect()
    else message('Live call unavailable. ' + friendly(e))
  } finally {
    connecting = false
  }
}

function wireRoom(sdk: any, r: any, target: string) {
  const E = sdk.RoomEvent
  const rerender = () => render()
  // A new arrival doesn't know our deafen state (it never hit the SFU) — re-announce.
  // ParticipantConnected/Disconnected fire only for people moving after we
  // joined — the roster present at connect arrives as initial state, so this
  // won't spam on join or reconnect.
  r.on(E.ParticipantConnected, () => { broadcastDeaf(); render(); playSfx('peerJoin') })
  r.on(E.ParticipantDisconnected, (p: any) => { remoteDeaf.delete(p?.identity || ''); render(); playSfx('peerLeave') })
  r.on(E.LocalTrackPublished, () => { render(); emitState() })
  r.on(E.LocalTrackUnpublished, () => { render(); emitState() })
  r.on(E.AudioPlaybackStatusChanged, () => { if (r === room && !r.canPlaybackAudio) armAudioUnlock() })
  r.on(E.ActiveSpeakersChanged, rerender)
  r.on(E.TrackMuted, rerender)
  r.on(E.TrackUnmuted, rerender)
  r.on(E.TrackSubscribed, (track: any, _pub: any, participant: any) => {
    if (track.kind === 'audio') {
      const a = track.attach()
      a.dataset.sid = participant.sid
      a.muted = deafened
      // livekit un-mutes attached elements behind our back: attachToElement()
      // sets muted = false whenever the SFU swaps the MediaStreamTrack in
      // (resume / renegotiation), and Room.startAudio() — fired when our own
      // mic (re)starts — unmutes every element at once. Either one let single
      // participants leak through a deafen. muted flips fire `volumechange`,
      // so re-assert the deafen the moment anything lifts it.
      a.addEventListener('volumechange', () => { if (deafened && !a.muted) a.muted = true })
      sink().appendChild(a)
    }
    render()
  })
  r.on(E.TrackUnsubscribed, (track: any) => { try { track.detach().forEach((el: HTMLElement) => el.remove()) } catch { /* */ } render() })
  r.on(E.DataReceived, (payload: Uint8Array, participant: any) => {
    try {
      const msg = JSON.parse(new TextDecoder().decode(payload))
      if (msg && msg.t === 'atrium.deaf') { remoteDeaf.set(participant?.identity || '', !!msg.deaf); render(); return }
      const from = mxidOf(participant?.identity || '')
      dataSubs.forEach((cb) => { try { cb(msg, from) } catch (e) { console.warn('[call] data handler', e) } })
    } catch { /* not ours */ }
  })
  // LiveKit resumes transient drops on its own (signal/ICE restart) without ever
  // firing Disconnected; media often keeps flowing throughout. Our state was
  // never torn down, so just re-sync tiles and re-publish presence on resume.
  r.on(E.Reconnected, () => { if (r !== room) return; render(); emitState(); startHeartbeat() })
  r.on(E.Disconnected, (reason?: any) => {
    // Only the room we're CURRENTLY in may drive call state. A late or duplicate
    // Disconnected from a room we've already replaced (classically the SFU
    // kicking the stale participant as DUPLICATE_IDENTITY once the reconnect
    // reuses this tab's identity) would otherwise null out the live `room` and
    // wedge the UI — the dock buttons vanish and "Reconnecting…" shows while the
    // new room's audio keeps flowing. Silence the dead room and ignore it.
    if (r !== room) { try { r.removeAllListeners() } catch { /* */ } return }
    stopHeartbeat()
    try { r.removeAllListeners() } catch { /* */ } // this room is done; it must stay silent
    // An unexpected drop while we still want this call (network blip, server
    // restart) → keep our presence and reconnect, rather than ending the call.
    if (desired?.id === target && recoverableDisconnect(sdk, reason)) {
      room = null
      callState.connected = false
      clearTiles() // drop the dead room's tiles/tracks; reconnect rebuilds them
      if (audioSink) audioSink.innerHTML = '' // dead room's audio elements too
      scheduleReconnect()
      return
    }
    message('Call ended.')
    room = null; curMatrixId = null
    // This call is over, so a reload must not walk back into it — unless the
    // "end" is just livekit tearing down because the page itself is going away.
    if (!pageLeaving) clearResume()
    emitState()
    // Presence is call membership: deliberately dropping out of the call clears
    // your m.rtc.member in the room. Idempotent with leaveCall's clear.
    clearRoomPresence(target)
  })
}

// clearRoomPresence retracts our occupancy signal (and, for team-room guests,
// our room membership) for the room we were calling in.
function clearRoomPresence(matrixRoomId: string | null) {
  if (!matrixRoomId) return
  const floor = useFloor()
  const slug = floor.slugOf(matrixRoomId)
  if (slug) void floor.leaveRoom(slug)
}

function allParticipants(): any[] {
  if (!room) return []
  const remotes = room.remoteParticipants ? Array.from(room.remoteParticipants.values()) : []
  return [room.localParticipant, ...remotes].filter(Boolean)
}

/* ---- ephemeral signals over the SFU data channel (reactions, spatial pos).
   Transient by design — they never touch Matrix history. ---- */
const dataSubs = new Set<(msg: any, from: string) => void>()

export function onCallData(cb: (msg: any, from: string) => void): () => void {
  dataSubs.add(cb)
  return () => dataSubs.delete(cb)
}

export function sendCallData(msg: Record<string, unknown>): void {
  if (!room || room.state !== 'connected') return
  try {
    room.localParticipant.publishData(new TextEncoder().encode(JSON.stringify(msg)), { reliable: false })
  } catch (e) { console.warn('[call] publishData failed', e) }
}

// Announce our sound-output (deafen) state to everyone. Reliable so it isn't
// dropped, and re-sent whenever someone new joins so late arrivals learn it.
function broadcastDeaf(): void {
  if (!room || room.state !== 'connected') return
  try {
    room.localParticipant.publishData(new TextEncoder().encode(JSON.stringify({ t: 'atrium.deaf', deaf: deafened })), { reliable: true })
  } catch { /* */ }
}

/** The live camera track for a participant — null while the camera is off,
 *  muted, or not yet subscribed. For views that render video outside the
 *  imperative tile stage (the watercooler bubbles): attach it to your own
 *  <video> and re-check whenever callState.mediaTick bumps. */
export function cameraTrackFor(mxid: string): any | null {
  if (!room || !LK) return null
  const p = allParticipants().find((x: any) => mxidOf(x.identity) === mxid)
  const pub = p && p.getTrackPublication && p.getTrackPublication(LK.Track.Source.Camera)
  return pub?.track && !pub.isMuted ? pub.track : null
}

/** Spatial audio: set each remote participant's playback volume (0..1).
 *  Returning null leaves that person untouched. */
export function applyVolumes(volumeFor: (mxid: string) => number | null): void {
  if (!room) return
  const remotes = room.remoteParticipants ? Array.from(room.remoteParticipants.values()) : []
  for (const p of remotes as any[]) {
    const v = volumeFor(mxidOf(p.identity))
    if (v === null) continue
    try { p.setVolume(Math.max(0, Math.min(1, v))) } catch { /* not playing audio yet */ }
  }
}
function hasUnmuted(p: any, source: any): boolean {
  const pub = p.getTrackPublication && p.getTrackPublication(source)
  return !!(pub && !pub.isMuted)
}

/* ---- stable tiles ----
   Tiles persist across renders, keyed by participant + source. A render
   updates them in place and reconciles DOM order; video tracks stay attached
   to the same <video> element throughout. (The previous innerHTML rebuild
   re-attached every stream on every room event — black flicker.) A screen
   share is its own tile next to the camera tile, never a replacement. */

type TileKind = 'cam' | 'screen'
interface TileEntry {
  el: HTMLElement
  bar: HTMLElement
  barKey: string
  videoWrap: HTMLElement | null
  videoEl: HTMLVideoElement | null
  track: any
  off: HTMLElement | null
  ring: HTMLElement | null
}
const tiles = new Map<string, TileEntry>()
let stageKey = '' // which layout skeleton currently fills the container

function ensureStage(key: string, html: string): void {
  if (!container) return
  if (stageKey === key && container.firstElementChild) return
  container.innerHTML = html
  stageKey = key
}

function dropVideo(t: TileEntry): void {
  if (t.track && t.videoEl) { try { t.track.detach(t.videoEl) } catch { /* */ } }
  if (t.videoEl) t.videoEl.remove()
  t.videoEl = null; t.track = null
}

function clearTiles(): void {
  tiles.forEach((t) => { dropVideo(t); t.el.remove() })
  tiles.clear()
  remoteDeaf.clear() // peers re-announce when we reconnect (we look new to them)
  stageKey = ''
}

function tileFor(sdk: any, p: any, kind: TileKind): HTMLElement {
  const key = `${p.sid}|${kind}`
  let t = tiles.get(key)
  if (!t) {
    const el = document.createElement('div')
    const bar = document.createElement('div'); bar.className = 'vtile__bar'
    el.appendChild(bar)
    if (kind === 'screen') {
      // With several shares up, clicking one brings it to the full-size stage.
      const sid = p.sid
      el.addEventListener('click', () => { pinnedShareSid = sid; render() })
      // Fullscreen toggle — delegated from the bar, which survives the
      // innerHTML rewrites updateTile does on state changes.
      bar.addEventListener('click', (e) => {
        if (!(e.target as HTMLElement).closest('.vtile__fs')) return
        e.stopPropagation()
        if (document.fullscreenElement === el) void document.exitFullscreen()
        else el.requestFullscreen().catch(() => { /* not allowed here */ })
      })
    }
    t = { el, bar, barKey: '', videoWrap: null, videoEl: null, track: null, off: null, ring: null }
    tiles.set(key, t)
  }
  updateTile(sdk, t, p, kind)
  return t.el
}

function updateTile(sdk: any, t: TileEntry, p: any, kind: TileKind): void {
  const isYou = p === room.localParticipant
  const speaking = !!p.isSpeaking
  const micPub = p.getTrackPublication && p.getTrackPublication(sdk.Track.Source.Microphone)
  const micOn = !!(micPub && !micPub.isMuted && (isYou ? true : micPub.isSubscribed !== false))
  const deaf = isYou ? deafened : !!remoteDeaf.get(p.identity)
  const source = kind === 'screen' ? sdk.Track.Source.ScreenShare : sdk.Track.Source.Camera
  const pub = p.getTrackPublication && p.getTrackPublication(source)
  const track = pub?.track && !pub.isMuted ? pub.track : null

  // Layout classes (vtile--spot / vtile--mini) are re-added by the layout pass.
  t.el.className = 'vtile' + (kind === 'screen' ? ' vtile--screen' : '') +
    (speaking && kind === 'cam' ? ' is-speaking' : '') + (isYou ? ' is-you' : '')
  t.el.style.setProperty('--c', personColor(p.identity))

  if (track) {
    const attached = t.track === track && t.videoEl && t.videoEl.parentElement === t.videoWrap
    if (!attached) {
      dropVideo(t)
      if (t.off) { t.off.remove(); t.off = null }
      if (!t.videoWrap) {
        t.videoWrap = document.createElement('div'); t.videoWrap.className = 'vtile__cam'
        t.el.insertBefore(t.videoWrap, t.bar)
      }
      const v = track.attach() as HTMLVideoElement
      if (kind === 'screen') {
        v.style.width = '100%'; v.style.height = '100%'; v.style.objectFit = 'contain'
      } else {
        // Always fill the tile's height and let the width spill over (cropping
        // left/right) or fall short (pillarboxing on the backdrop). A wide tile
        // then never zooms in and clips the top/bottom of someone's face.
        v.style.height = '100%'; v.style.width = 'auto'; v.style.maxWidth = 'none'; v.style.objectFit = 'cover'
        if (isYou) v.style.transform = 'scaleX(-1)'
      }
      t.videoWrap.appendChild(v)
      t.videoEl = v; t.track = track
    }
  } else {
    dropVideo(t)
    if (t.videoWrap) { t.videoWrap.remove(); t.videoWrap = null }
    if (!t.off) {
      const off = document.createElement('div'); off.className = 'vtile__off'
      off.innerHTML = `<span class="call-ini" style="--c:${personColor(p.identity)}">${initials(p.identity)}</span>`
      t.el.insertBefore(off, t.bar)
      t.off = off
      // Custom avatar, when they have one: swap in once the authenticated
      // media fetch resolves (initials hold the tile until then).
      const mxc = useFloor().people[mxidOf(p.identity)]?.avatarUrl
      if (mxc) {
        void matrix.mediaObjectUrl(mxc).then((url) => {
          if (url && t.off === off) {
            off.innerHTML = `<img class="call-ava" style="--c:${personColor(p.identity)}" src="${url}" alt="" draggable="false" />`
          }
        })
      }
    }
  }

  const barKey = `${personName(p.identity)}|${isYou}|${micOn}|${deaf}|${kind}`
  if (barKey !== t.barKey) {
    t.barKey = barKey
    const label = esc(personName(p.identity)) + (isYou ? ' (you)' : '') + (kind === 'screen' ? ' · screen' : '')
    // A screen tile shows the share badge; a person tile shows mic, plus a
    // sound-off badge when they've muted their own ears (can't hear the room).
    const status = kind === 'screen'
      ? `<button class="vtile__fs" title="View fullscreen (Esc leaves)">${rawIcon('expand')}</button><span class="vtile__mic">${rawIcon('screen')}</span>`
      : `${deaf ? `<span class="vtile__mic off" title="Sound off — not listening">${rawIcon('soundOff')}</span>` : ''}<span class="vtile__mic ${micOn ? '' : 'off'}">${rawIcon(micOn ? 'mic' : 'micOff')}</span>`
    t.bar.innerHTML = `<span class="vtile__name">${label}</span><span class="vtile__status">${status}</span>`
  }

  const wantRing = speaking && kind === 'cam'
  if (wantRing && !t.ring) { const ring = document.createElement('span'); ring.className = 'vtile__ring'; t.el.appendChild(ring); t.ring = ring }
  if (!wantRing && t.ring) { t.ring.remove(); t.ring = null }
}

// Drop tiles whose participant left or whose screen share ended.
function pruneTiles(sdk: any, parts: any[]): void {
  const want = new Set<string>()
  for (const p of parts) {
    want.add(`${p.sid}|cam`)
    if (hasUnmuted(p, sdk.Track.Source.ScreenShare)) want.add(`${p.sid}|screen`)
  }
  tiles.forEach((t, key) => {
    if (want.has(key)) return
    dropVideo(t); t.el.remove(); tiles.delete(key)
  })
}

// Match parent's children to els (order included) moving as little as possible.
function reconcile(parent: HTMLElement, els: HTMLElement[]): void {
  const keep = new Set(els)
  for (const child of Array.from(parent.children)) if (!keep.has(child as HTMLElement)) child.remove()
  let cursor = parent.firstElementChild
  for (const el of els) {
    if (el === cursor) { cursor = cursor.nextElementSibling; continue }
    parent.insertBefore(el, cursor)
  }
  // Moving a <video> between parents can pause it; nudge it back.
  for (const el of els) {
    const v = el.querySelector('video') as HTMLVideoElement | null
    if (v && v.paused) v.play().catch(() => { /* autoplay policy */ })
  }
}

// Camera tiles fill their height and trim the sides (never the top/bottom). To
// keep that flattering we want each tile's aspect between portrait 9:16 and
// landscape 16:9: never wider than the camera (so the video fills the width or
// spills over — never letterboxes) and never so narrow it slices the face.
const GRID_GAP = 8   // .vstage gap
const GRID_PAD = 8   // .vstage padding
const TILE_A_MAX = 16 / 9
const TILE_A_MIN = 9 / 16

// Pick the column count (and resulting tile size) that shows people as large as
// possible for n participants in a w×h box. Tiles fill their cell unless that
// cell is wider/narrower than the allowed aspect band, in which case the tile is
// trimmed and centred (the leftover shows the stage backdrop, not a stretched
// or letterboxed video). Classic largest-tile search, biased portrait by the
// band so wide stages produce taller tiles rather than letterboxed wide ones.
function gridLayout(n: number, w: number, h: number): { cols: number; tileW: number; tileH: number } {
  let best = { cols: 1, tileW: 0, tileH: 0, area: -1 }
  for (let cols = 1; cols <= n; cols++) {
    const rows = Math.ceil(n / cols)
    const cw = (w - GRID_GAP * (cols - 1)) / cols
    const ch = (h - GRID_GAP * (rows - 1)) / rows
    if (cw <= 0 || ch <= 0) continue
    let tw = cw, th = ch
    const asp = cw / ch
    if (asp > TILE_A_MAX) tw = ch * TILE_A_MAX      // too wide → trim the width
    else if (asp < TILE_A_MIN) th = cw / TILE_A_MIN // too tall → trim the height
    const area = tw * th
    if (area > best.area) best = { cols, tileW: tw, tileH: th, area }
  }
  return best
}

// Size the camera grid to the stage. Desktop only — the mobile stage has no
// bounded height (it scrolls), so there it keeps the static count-based classes
// and their responsive fixed-row heights.
function layoutAutoGrid(grid: HTMLElement, n: number): void {
  const desktop = typeof window !== 'undefined' && window.matchMedia('(min-width: 821px)').matches
  const w = grid.clientWidth, h = grid.clientHeight
  if (!desktop || n < 1 || w < 4 || h < 4) {
    grid.className = `vstage grid-${Math.min(Math.max(n, 1), 6)}`
    grid.style.gridTemplateColumns = ''
    grid.style.gridAutoRows = ''
    return
  }
  const { cols, tileW, tileH } = gridLayout(n, w - GRID_PAD * 2, h - GRID_PAD * 2)
  grid.className = 'vstage vstage--auto'
  grid.style.gridTemplateColumns = `repeat(${cols}, ${Math.floor(tileW)}px)`
  grid.style.gridAutoRows = `${Math.floor(tileH)}px`
}

// Re-run the auto layout against the grid's current size and tile count. Coalesced
// through rAF so a burst of ResizeObserver callbacks does one relayout per frame.
function relayoutGrid(): void {
  gridRaf = 0
  if (!container) return
  const grid = container.querySelector('#call-tiles') as HTMLElement | null
  if (grid) layoutAutoGrid(grid, grid.childElementCount)
}
function scheduleRelayout(): void {
  if (gridRaf || typeof requestAnimationFrame !== 'function') return
  gridRaf = requestAnimationFrame(relayoutGrid)
}

function render() {
  if (!room) return
  const sdk = LK
  const parts = allParticipants()
  // The reactive roster: spatial/spotlight components draw from this.
  callState.participants = parts.map((p): CallParticipant => {
    const isYou = p === room.localParticipant
    const micPub = p.getTrackPublication && p.getTrackPublication(sdk.Track.Source.Microphone)
    return {
      mxid: mxidOf(p.identity), you: isYou, speaking: !!p.isSpeaking,
      micOn: !!(micPub && !micPub.isMuted && (isYou ? true : micPub.isSubscribed !== false)),
      camOn: hasUnmuted(p, sdk.Track.Source.Camera),
      screen: hasUnmuted(p, sdk.Track.Source.ScreenShare),
      deaf: isYou ? deafened : !!remoteDeaf.get(p.identity),
    }
  })
  callState.mediaTick++
  // m.rtc.member is keyed by user, not device: remember whether another of MY
  // devices/tabs shares this call, so leaving here doesn't clear presence that
  // is now theirs (floor.leaveRoom consults this).
  const me = mxidOf(room.localParticipant?.identity || '')
  usePresence().otherDeviceInCallRoom =
    parts.some((p) => p !== room.localParticipant && mxidOf(p.identity) === me) ? curMatrixId : null
  if (!container) return
  if (mountOpts.layout === 'hidden') { container.innerHTML = ''; stageKey = ''; return }
  if (!parts.length) { message('You’re the first one here. Waiting for others…'); return }
  pruneTiles(sdk, parts)
  const sharers = parts.filter((p) => hasUnmuted(p, sdk.Track.Source.ScreenShare))
  if (pinnedShareSid && !sharers.some((p) => p.sid === pinnedShareSid)) pinnedShareSid = null
  if (mountOpts.layout === 'spotlight') { renderSpotlight(sdk, parts, sharers); return }
  if (sharers.length) { renderShareStage(sdk, parts, sharers); return }
  ensureStage('grid', '<div class="vstage" id="call-tiles"></div>')
  const grid = container.querySelector('#call-tiles') as HTMLElement
  reconcile(grid, parts.map((p) => tileFor(sdk, p, 'cam')))
  // Re-fit now (so a join/leave restacks immediately) and again next frame, in
  // case the stage wasn't at its final size at this instant (view animating in,
  // panel toggling). Without the re-fit a new tile inherits the previous count's
  // row height and lands off the bottom of the stage.
  layoutAutoGrid(grid, parts.length)
  scheduleRelayout()
}

/** Grid rooms while someone shares: ONE share owns the stage in full
 *  (object-fit: contain — yours to pick when several people share), the other
 *  shares wait as clickable minis, and every camera — the sharer's included —
 *  stays live in a strip below. */
function renderShareStage(sdk: any, parts: any[], sharers: any[]) {
  if (!container) return
  ensureStage('share', '<div class="vstage vstage--share"><div class="vshare-main" id="share-main"></div><div class="vshare-strip" id="share-strip"></div></div>')
  const main = container.querySelector('#share-main') as HTMLElement
  const strip = container.querySelector('#share-strip') as HTMLElement
  const lead = sharers.find((p) => p.sid === pinnedShareSid) ||
    sharers.find((p) => p.sid === lastMainSid) || sharers[0]
  lastMainSid = lead.sid
  const stage = tileFor(sdk, lead, 'screen'); stage.removeAttribute('title')
  reconcile(main, [stage])
  const waiting = sharers.filter((p) => p !== lead).map((p) => miniShare(sdk, p))
  reconcile(strip, [...waiting, ...parts.map((p) => { const el = tileFor(sdk, p, 'cam'); el.classList.add('vtile--mini'); return el })])
}

// A not-on-stage screen share: a mini tile in the strip, click to swap it in.
function miniShare(sdk: any, p: any): HTMLElement {
  const el = tileFor(sdk, p, 'screen')
  el.classList.add('vtile--mini', 'vtile--pick')
  el.title = 'Show this screen share full size'
  return el
}

/** Stage rooms: a screen share owns the spotlight (cameras keep their strip
 *  tiles); otherwise whoever is talking; otherwise the spotlight stays where
 *  it was (no flicker). */
function renderSpotlight(sdk: any, parts: any[], sharers: any[]) {
  if (!container) return
  ensureStage('spot', '<div class="auditorium"><div class="audi-stage" id="spot-main"></div><div class="audi-floor"><div class="audi-grid" id="spot-strip"></div></div></div>')
  const mainEl = container.querySelector('#spot-main') as HTMLElement
  const strip = container.querySelector('#spot-strip') as HTMLElement
  const mini = (p: any) => { const el = tileFor(sdk, p, 'cam'); el.classList.add('vtile--mini'); return el }
  if (sharers.length) {
    // Keep the chosen (or same) share on stage while it lasts; other shares
    // join the strip as clickable minis, all cameras follow.
    const sharer = sharers.find((p) => p.sid === pinnedShareSid) ||
      sharers.find((p) => p.sid === lastMainSid) || sharers[0]
    lastMainSid = sharer.sid
    const spot = tileFor(sdk, sharer, 'screen'); spot.classList.add('vtile--spot'); spot.removeAttribute('title')
    reconcile(mainEl, [spot])
    const waiting = sharers.filter((p) => p !== sharer).map((p) => miniShare(sdk, p))
    reconcile(strip, [...waiting, ...parts.map(mini)])
    return
  }
  const score = (p: any): number => (p.isSpeaking ? 2 : p.sid === lastMainSid ? 1 : 0)
  const main = parts.reduce((best, p) => (score(p) > score(best) ? p : best), parts[0])
  lastMainSid = main.sid
  const spot = tileFor(sdk, main, 'cam'); spot.classList.add('vtile--spot')
  reconcile(mainEl, [spot])
  reconcile(strip, parts.filter((p) => p !== main).map(mini))
}

function message(msg: string) {
  callState.message = msg
  stageKey = ''
  if (container) container.innerHTML = `<div class="call-msg"><span class="spinner"></span><span>${esc(msg)}</span></div>`
}
function friendly(e: any): string {
  const m = (e && (e.error || e.message)) || ''
  if (/token|forbidden|401|403/i.test(m)) return 'Sign-in needed.'
  if (/import|fetch|network/i.test(m)) return 'Could not load the call engine (offline?).'
  return 'See console for details.'
}

function readState() {
  if (!room || room.state !== 'connected') return { connected: false, mic: false, cam: false, screen: false, count: 0 }
  const lp = room.localParticipant
  const has = (src: any) => { const pub = lp.getTrackPublication && lp.getTrackPublication(src); return !!(pub && !pub.isMuted) }
  return {
    connected: true,
    mic: has(LK.Track.Source.Microphone),
    cam: has(LK.Track.Source.Camera),
    screen: has(LK.Track.Source.ScreenShare),
    count: allParticipants().length,
  }
}
function emitState() {
  const s = readState()
  const floor = useFloor()
  const slug = floor.activeSlug
  Object.assign(callState, s, { roomSlug: slug, roomName: (slug && floor.roomBySlug(slug)?.name) || 'call' })
  const media = s.connected ? { room: slug, mic: s.mic, cam: s.cam, screen: s.screen } : null
  const key = media ? `${slug}|${s.mic}|${s.cam}|${s.screen}` : 'off'
  if (key === lastMedia) return
  lastMedia = key
  usePresence().myMedia = media
  if (s.connected && slug) void floor.publishMedia(slug, { mic: s.mic, cam: s.cam, screen: s.screen })
}

export async function setMic(on: boolean, opts: { silent?: boolean } = {}) {
  micMuted = !on // remember the choice so a reconnect/reload doesn't override it
  persistMute()
  if (!room) return
  const was = callState.mic
  try { await room.localParticipant.setMicrophoneEnabled(on) } catch { /* */ }
  render(); emitState()
  if (!opts.silent && callState.mic !== was) playSfx(callState.mic ? 'unmute' : 'mute')
}
export async function setCam(on: boolean) { if (room) { try { await room.localParticipant.setCameraEnabled(on) } catch { /* */ } render(); emitState() } }
/* Screen-share capture + publish options per quality preference.
   The defaults blur text because no contentHint is set (the encoder optimises
   for motion) — 'detail'/'text' hints plus a maintain-resolution degradation
   preference keep glyphs crisp and drop framerate instead under congestion.
   Capture resolution width/height of 0 means "uncapped/native"; we instead cap
   with a high ideal so framerate is also capped at the source. */
function screenShareOpts(q: string): { capture: any; publish: any } {
  switch (q) {
    case 'sharp': // source resolution, sharpest text, spare no bitrate, 15fps
      return {
        capture: { resolution: { width: 3840, height: 2160, frameRate: 15 }, contentHint: 'text' },
        publish: { screenShareEncoding: { maxBitrate: 12_000_000, maxFramerate: 15, priority: 'high' }, degradationPreference: 'maintain-resolution', simulcast: false },
      }
    case 'p1080':
      return {
        capture: { resolution: { width: 1920, height: 1080, frameRate: 15 }, contentHint: 'detail' },
        publish: { screenShareEncoding: { maxBitrate: 3_000_000, maxFramerate: 15, priority: 'high' }, degradationPreference: 'maintain-resolution', simulcast: false },
      }
    case 'p720':
      return {
        capture: { resolution: { width: 1280, height: 720, frameRate: 15 }, contentHint: 'detail' },
        publish: { screenShareEncoding: { maxBitrate: 1_500_000, maxFramerate: 15 }, degradationPreference: 'maintain-resolution', simulcast: false },
      }
    case 'motion': // full-motion video, smooth, lets resolution drop
      return {
        capture: { resolution: { width: 1920, height: 1080, frameRate: 30 }, contentHint: 'motion' },
        publish: { screenShareEncoding: { maxBitrate: 4_000_000, maxFramerate: 30 }, degradationPreference: 'maintain-framerate' },
      }
    case 'auto':
    default:
      // Single source-resolution layer (NOT simulcast): with simulcast on, the
      // subscriber's adaptiveStream picks the layer matching the on-screen tile
      // size — usually well below native — so text never reaches source clarity.
      // A single layer can't be downscaled server-side, so it always arrives
      // sharp; maintain-resolution then sheds framerate/bitrate (not resolution)
      // under congestion. Slightly lower ceiling than 'sharp' to stay friendly.
      return {
        capture: { resolution: { width: 3840, height: 2160, frameRate: 15 }, contentHint: 'text' },
        publish: { screenShareEncoding: { maxBitrate: 8_000_000, maxFramerate: 15, priority: 'high' }, degradationPreference: 'maintain-resolution', simulcast: false },
      }
  }
}

export async function setScreen(on: boolean) {
  if (!room) return
  try {
    if (on) {
      const { capture, publish } = screenShareOpts(usePrefs().screenQuality)
      await room.localParticipant.setScreenShareEnabled(true, capture, publish)
    } else {
      await room.localParticipant.setScreenShareEnabled(false)
    }
  } catch { /* */ }
  render(); emitState()
}

/** Mute/unmute sound OUTPUT (your ears). Local-only: mutes the attached audio
 *  elements, so it composes with spatial setVolume and never touches the SFU. */
export function setDeafen(on: boolean): void {
  const was = deafened
  deafened = on
  callState.deafen = on
  persistMute() // remembered across reconnects/reloads alongside the mic choice
  sink().querySelectorAll('audio').forEach((a) => { (a as HTMLAudioElement).muted = on })
  broadcastDeaf() // let the room mark our tile as not-listening
  render()
  if (on === was) return
  playSfx(on ? 'deafen' : 'undeafen')
  // Deafening means you've stepped out of the conversation, so mute the mic too;
  // undeafening returns the mic to whatever it was before you deafened.
  if (on) {
    micMutedBeforeDeafen = micMuted
    if (!micMuted) void setMic(false, { silent: true })
  } else {
    if (micMutedBeforeDeafen === false) void setMic(true, { silent: true })
    micMutedBeforeDeafen = null
  }
}

/* ---- devices ----
   LiveKit fronts enumerateDevices/setSinkId; choices persist in prefs and are
   fed back in as capture/output defaults on the next connect. */

export type DeviceKind = 'audioinput' | 'videoinput' | 'audiooutput'

/** Output device picking needs setSinkId (no Safari). */
export function canPickSpeaker(): boolean {
  return typeof HTMLMediaElement !== 'undefined' && 'setSinkId' in HTMLMediaElement.prototype
}

/** Enumerate devices of a kind (asks for permission so labels are real). */
export async function listDevices(kind: DeviceKind): Promise<MediaDeviceInfo[]> {
  const sdk = await loadSDK()
  try { return await sdk.Room.getLocalDevices(kind, true) } catch { return [] }
}

/** Switch the live call to a device; no-op when not connected (the pref still
 *  applies on the next connect). */
export async function setDevice(kind: DeviceKind, deviceId: string): Promise<void> {
  if (!room || room.state !== 'connected') return
  if (kind === 'audiooutput' && !canPickSpeaker()) return
  try { await room.switchActiveDevice(kind, deviceId || 'default') } catch (e) { console.warn('[call] switch device', kind, e) }
}

/** The device the live call is actually using ('' when unknown). */
export function activeDevice(kind: DeviceKind): string {
  try { return (room && room.state === 'connected' && room.getActiveDevice(kind)) || '' } catch { return '' }
}

export async function leaveCall() {
  const prevRoomId = curMatrixId
  if (callState.connected) playSfx('selfLeave')
  // Last human out concludes a live recording right away, while this client is
  // still connected and therefore authorized to stop it. The capture
  // supervisor's empty-call poll remains the backstop for closed tabs.
  if (prevRoomId && room && callState.connected) {
    const remoteHumans = room.remoteParticipants
      ? Array.from(room.remoteParticipants.values()).filter((p: any) => String(p.identity || '').startsWith('@')).length
      : 0
    if (remoteHumans === 0) {
      const capture = useCapture()
      const sess = capture.session(prevRoomId)
      if (sess && (sess.state === 'recording' || sess.state === 'pending_consent')) {
        try { await capture.stop(prevRoomId) } catch { /* the supervisor will conclude it */ }
      }
    }
  }
  desired = null // deliberate leave: cancel any pending/auto reconnect
  reconnectTries = 0
  clearReconnect()
  clearResume() // and don't rejoin after a reload either
  stopHeartbeat()
  gridObs?.disconnect(); gridObs = null
  if (gridRaf) { cancelAnimationFrame(gridRaf); gridRaf = 0 }
  try { if (room) await room.disconnect() } catch { /* */ }
  room = null; curMatrixId = null; lastMedia = ''; lastMainSid = null; pinnedShareSid = null
  micMutedBeforeDeafen = null // the deafen↔mic coupling context ends with the call
  clearTiles()
  usePresence().myMedia = null
  // mic-muted / deafen are intentionally NOT reset here — they're remembered
  // (persistMute) so a later rejoin honours them; only live flags reset.
  Object.assign(callState, { connected: false, mic: false, cam: false, screen: false, deafen: deafened, count: 0, roomSlug: null, participants: [] })
  if (audioSink) audioSink.innerHTML = ''
  clearRoomPresence(prevRoomId)
}

export function isConnected(): boolean { return !!(room && room.state === 'connected') }
